import React, { useState } from "react";
import { css } from "@emotion/react";
import PropTypes from "prop-types";
import Form, { CheckBox, SIZE, Label } from "components/Form";
import { OperatorToggle } from "components/Search";
import { Grid, Row } from "components/Containers";
import Expandable from "components/Expandable";
import { TOGGLE_SIZE } from "components/Form/Toggle";
import { useAuth, useAdvancedFilters } from "hooks";

/**
 * BaseAdvancedFilters
 *
 * @param {Function}  setSelected
 * @param {Object}    formValues
 * @param {Object}    selected
 * @param {Boolean}   loading
 * @param {Array}     initialStickyFilterKeys   // Allows to set some filters as fixed on the UI
 * @param {Object}    filterConfigMap          // Map of filter configurations
 */
const BaseAdvancedFilters = ({
  setSelected,
  formValues,
  selected,
  loading,
  initialStickyFilterKeys,
  filterConfigMap,
  ...props
}) => {
  const { roles, isShowcase } = useAuth();
  const [isExpanded, setIsExpanded] = useState(false);

  const {
    enabledFiltersFromUrl,
    stickyFilterKeys,
    filterConfig,
    stickyFilters,
    operators,
    handleToggle,
    handleOperatorChange,
    handleChange,
  } = useAdvancedFilters({
    selected,
    initialStickyFilterKeys,
    filterConfigMap,
    setSelected,
    roles,
  });

  const formProps = {
    initialValues: selected.advancedFilters,
    hideOptionalLabel: true,
    disabled: loading,
    onChange: handleChange,
  };

  const commonFormArgs = {
    operators,
    handleToggle,
    handleOperatorChange,
    formValues,
    enabledFilterKeys: Object.keys(enabledFiltersFromUrl),
  };

  return (
    <>
      {!isExpanded && stickyFilterKeys.length > 0 && (
        <Grid cols={4}>
          <Grid.col colSpan={4}>
            <Form data={FORM_COLUMNS(stickyFilters, commonFormArgs, stickyFormStyles)} {...formProps} {...props} />
          </Grid.col>
        </Grid>
      )}
      <Grid cols={4}>
        <Grid.col colSpan={4}>
          <Expandable onToggle={(expanded) => setIsExpanded(expanded)}>
            {isExpanded ? (
              <Form
                data={FORM_COLUMNS(filterConfig, commonFormArgs, expandableFormStyles, { isShowcase })}
                {...formProps}
                {...props}
              />
            ) : null}
          </Expandable>
        </Grid.col>
      </Grid>
    </>
  );
};

const FORM_COLUMNS = (
  filters = {},
  { operators, handleToggle, handleOperatorChange, formValues = {}, enabledFilterKeys = [] },
  style,
  context = {}
) => [
  {
    style,
    items: Object.entries(filters).map(([key, { optionsFunc, showOperator, ...config }]) => ({
      ...config,
      label: (
        <Row>
          <CheckBox
            value={enabledFilterKeys.includes(key)}
            onChange={handleToggle}
            name={`${key}[enabled]`}
            size={TOGGLE_SIZE.small}
            key={key}
            label={<Label>{typeof config.label === "string" ? config.label : config.label(context)}</Label>}
          />
        </Row>
      ),
      size: SIZE.small,
      parentObjectKey: key,
      hint: showOperator && (
        <OperatorToggle
          value={operators[key]}
          name={`${key}[operator]`}
          size={TOGGLE_SIZE.small}
          onChange={handleOperatorChange}
        />
      ),
      properties: {
        key,
        name: "value",
        ...config.properties,
        ...(optionsFunc && { options: optionsFunc(formValues) }),
      },
    })),
  },
];

const stickyFormStyles = css`
  width: 100%;
  padding: 0;
  margin: 0;
  margin-top: 1rem;
`;

const expandableFormStyles = css`
  width: 100%;
  padding: 0;
  margin: 0;
`;

BaseAdvancedFilters.defaultProps = {
  initialStickyFilterKeys: [],
};

BaseAdvancedFilters.propTypes = {
  setSelected: PropTypes.func.isRequired,
  formValues: PropTypes.object,
  selected: PropTypes.object.isRequired,
  loading: PropTypes.bool,
  initialStickyFilterKeys: PropTypes.array,
  filterConfigMap: PropTypes.object.isRequired,
};

export default BaseAdvancedFilters;
